import { Component, HostListener, OnDestroy, OnInit, effect, input, output, signal, untracked } from '@angular/core';
import { ChartConstants } from '@constants/pages/chart-constants';
import { PieChartAggregationStrategy } from '@constants/pages/user-defined-page/pie-chart-aggregation-strategy';
import { ChartPieType } from '@constants/pages/user-defined-page/chart-pie-type';
import { UserDefinedWidgetType } from '@constants/pages/user-defined-page/user-defined-widget-type';
import { Signal } from '@models/dashboard/signal';
import { ChartSignalEntry, Colors } from '@models/pages/user-defined-page/chart-signal-entry';
import { UserDefinedWidget } from '@models/pages/user-defined-page/user-defined-widget';
import { TranslateService } from '@ngx-translate/core';
import { SortingService } from '@services/administration/sorting/sorting.service';
import { ChartsConfigurationHelperService } from '@services/charts/helper/charts-configuration-helper.service';
import { NotificationService } from '@services/notification/notification.service';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs';

@Component({
    selector: 'scitis-pie-widget-configuration',
    templateUrl: './pie-widget-configuration.component.html',
    styleUrls: ['./pie-widget-configuration.component.scss'],
    standalone: false
})
export class PieWidgetConfigurationComponent implements OnInit, OnDestroy {
  userDefinedWidget = input<UserDefinedWidget>(new UserDefinedWidget());
  machineSignals = input<Signal[]>([]);
  isTemplateWidget = input<boolean>(false);

  isConfigurationValid = output<boolean>();
  hasDeletedSignalsChanged = output<boolean>();

  selectedSignalIds = signal<number[]>([]);
  usableSignals = signal<Signal[]>([]);
  hasDeletedSignalsMap = signal<Map<string, boolean>>(new Map<string, boolean>());
  isValidTimePeriod = signal<boolean>(true);

  dragAndDropItems = 'PIE_CHART_SIGNALS';
  subscription = new Subscription();

  readonly pieChartTypes: ChartPieType[] = [ChartPieType.STANDARD, ChartPieType.DONUT, ChartPieType.HALF_DONUT];
  readonly aggregationStrategies: PieChartAggregationStrategy[] = [PieChartAggregationStrategy.AVERAGE, PieChartAggregationStrategy.LATEST];

  private readonly DEFAULT_TIME_PERIOD_MINUTES = 5;

  public get isPieWidget(): boolean {
    return this.userDefinedWidget().widgetDetails.widgetType === UserDefinedWidgetType.PIE;
  }

  public get isTimePeriodDisabled(): boolean {
    return this.isSelectedAggregationStrategy(PieChartAggregationStrategy.LATEST);
  }

  constructor(private translate: TranslateService,
              private notification: NotificationService,
              private chartsConfigurationHelperService: ChartsConfigurationHelperService,
              private sortingService: SortingService,
              private dragulaService: DragulaService) {
    this.initializeDragAndDropSubscriptionEvents();
    this.chartsConfigurationHelperService.hasDeletedSignals$.subscribe(hasDeletedSignals => {
      this.hasDeletedSignalsMap.set(hasDeletedSignals);
    });

    effect(() => {
      this.userDefinedWidget();
      this.usableSignals();
      untracked(() => {
        if (this.usableSignals().length > 0) {
          this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap().set(String(this.userDefinedWidget().id), this.hasDeletedSignals()));
          this.validateConfigurationAndEmitEvent();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.userDefinedWidget().userDefinedChartDetails.signals ??= [];
    this.usableSignals.set([...this.chartsConfigurationHelperService.filterMisconfiguredOrUnusableSignals(this.machineSignals())]);
    this.applyDefaultSettings();
    this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap().set(String(this.userDefinedWidget().id), this.hasDeletedSignals()));
    this.validateConfigurationAndEmitEvent();
  }

  @HostListener('document:keydown.escape') onKeydownHandler() {
    this.dragulaService.destroy(this.dragAndDropItems);
  }

  onPieChartTypeSelected(pieChartType: ChartPieType) {
    this.userDefinedWidget().userDefinedChartDetails.pieChartType = pieChartType;
    this.validateConfigurationAndEmitEvent();
  }

  onAggregationStrategySelected(strategy: PieChartAggregationStrategy) {
    this.userDefinedWidget().userDefinedChartDetails.aggregationStrategy = strategy;
    this.validateConfigurationAndEmitEvent();
  }

  onTimePeriodChanged($event: number) {
    this.userDefinedWidget().userDefinedChartDetails.rangeValue = $event;
    this.isValidTimePeriod.set(this.verifyTimePeriod($event));
    this.validateConfigurationAndEmitEvent();
  }

  onSignalSelected($event: Signal | Signal[]) {
    const chartDetails = this.userDefinedWidget().userDefinedChartDetails;
    const selectedEntries = new Map((chartDetails.signals ?? []).map(signal => [signal.signalId, signal]));
    chartDetails.signals = [];
    if (!$event) {
      this.validateConfigurationAndEmitEvent();
      return;
    }

    const signals: Signal[] = Array.isArray($event) ? $event : [$event];
    for (const signal of signals) {
      if (this.shouldShowTooManySignalWarning()) {
        continue;
      }

      const existingSignal = selectedEntries.get(signal.id);
      const chartSignal = existingSignal ?? new ChartSignalEntry(signal);
      if (!existingSignal) {
        chartSignal.colors = new Colors();
        const normalizedDefaultColor = this.chartsConfigurationHelperService.normalizeColorToHex(signal.signalColor);
        chartSignal.colors.main = this.chartsConfigurationHelperService.convertHexColorToNumber(normalizedDefaultColor);
      }
      chartDetails.signals = [...chartDetails.signals, chartSignal];
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap().set(String(this.userDefinedWidget().id), this.hasDeletedSignals()));
    }
    this.validateConfigurationAndEmitEvent();
  }

  sortByLabel(signals: ChartSignalEntry[]): ChartSignalEntry[] {
    return this.sortingService.sortArrayByProperty(signals, 'label', true);
  }

  hasSignalBeenDeleted(signal: ChartSignalEntry): boolean {
    return !this.usableSignals().find(s => s.id == signal.signalId);
  }

  isSelectedPieChartType(pieChartType: ChartPieType): boolean {
    return (this.userDefinedWidget().userDefinedChartDetails.pieChartType || ChartPieType.STANDARD) === pieChartType;
  }

  isSelectedAggregationStrategy(strategy: PieChartAggregationStrategy): boolean {
    return (this.userDefinedWidget().userDefinedChartDetails.aggregationStrategy || PieChartAggregationStrategy.LATEST) === strategy;
  }

  getPieChartTypeLabel(pieChartType: ChartPieType): string {
    switch (pieChartType) {
      case ChartPieType.DONUT:
        return this.translate.instant('Donut');
      case ChartPieType.HALF_DONUT:
        return this.translate.instant('Half Donut');
      case ChartPieType.VARIABLE_RADIUS:
        return this.translate.instant('Variable radius');
      default:
        return this.translate.instant('Standard');
    }
  }

  getAggregationStrategyLabel(strategy: PieChartAggregationStrategy): string {
    switch (strategy) {
      case PieChartAggregationStrategy.AVERAGE:
        return this.translate.instant('Average');
      default:
        return this.translate.instant('Latest value');
    }
  }

  public onSignalRemove(signal: ChartSignalEntry) {
    const chartDetails = this.userDefinedWidget().userDefinedChartDetails;
    chartDetails.signals = (chartDetails.signals ?? []).filter(entry => entry.signalId !== signal.signalId);
    this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap().set(String(this.userDefinedWidget().id), this.hasDeletedSignals()));
    this.validateConfigurationAndEmitEvent();
  }

  public onSignalColorChanged(color: string, signal: ChartSignalEntry) {
    if (color && signal) {
      const normalizedColor = this.chartsConfigurationHelperService.normalizeColorToHex(color);
      const colorNumber = this.chartsConfigurationHelperService.convertHexColorToNumber(normalizedColor);
      this.userDefinedWidget().userDefinedChartDetails.signals =
        this.userDefinedWidget().userDefinedChartDetails.signals.map((s: ChartSignalEntry) => {
          if (s.signalId !== signal.signalId) {
            return s;
          }
          return {...s, colors: {...s.colors, main: colorNumber}};
        });
      this.validateConfigurationAndEmitEvent();
    }
  }

  public getSignalOriginalColor(signal: ChartSignalEntry): string {
    if (!signal || !signal.signalId) {
      return ChartConstants.DEFAULT_LINE_COLOR;
    }
    const machineSignal = this.usableSignals().find(s => s.id === signal.signalId);
    if (!machineSignal || !machineSignal.signalColor) {
      return ChartConstants.DEFAULT_LINE_COLOR;
    }
    return machineSignal.signalColor;
  }

  private applyDefaultSettings() {
    const chartDetails = this.userDefinedWidget().userDefinedChartDetails;
    if (!chartDetails.pieChartType) {
      chartDetails.pieChartType = ChartPieType.STANDARD;
    }
    if (!chartDetails.aggregationStrategy) {
      chartDetails.aggregationStrategy = PieChartAggregationStrategy.LATEST;
    }
    if (!chartDetails.rangeValue || chartDetails.rangeValue < 1) {
      chartDetails.rangeValue = this.DEFAULT_TIME_PERIOD_MINUTES;
    }
  }

  private hasDeletedSignals() {
    return (this.userDefinedWidget().userDefinedChartDetails.signals ?? []).some(signal =>
      signal.signalId > 0 && !this.usableSignals().find(s => s.id == signal.signalId)
    );
  }

  private shouldShowTooManySignalWarning(): boolean {
    if (this.userDefinedWidget().userDefinedChartDetails.signals.length < ChartConstants.MAX_NUMBER_OF_SIGNAL_ON_PIE_WIDGET) {
      return false;
    }

    this.notification.error(this.translate.instant('There are too many selected signals'), null, 'PieWidgetConfigurationComponent', true);
    return true;
  }

  private validateConfigurationAndEmitEvent() {
    let selectedSignalIds: number [] = [];
    this.setSelectedSignals();
    this.hasDeletedSignalsChanged.emit(this.hasDeletedSignals());
    const {userDefinedChartDetails} = this.userDefinedWidget();
    if (!userDefinedChartDetails || !userDefinedChartDetails.signals || userDefinedChartDetails.signals.length === 0) {

      this.selectedSignalIds.set([...selectedSignalIds]);
      const validConfiguration: boolean = this.verifyConfiguration();
      this.isConfigurationValid.emit(validConfiguration);
      return;
    }

    if (this.isTemplateWidget()) {
      const selectedSignals = userDefinedChartDetails.signals.map(signal => {
        return {signalName: signal.signalName};
      });
      selectedSignalIds = this.chartsConfigurationHelperService.getSelectedSignalIds(selectedSignals, this.usableSignals());
    } else {
      const chartSignalEntries: ChartSignalEntry[] = userDefinedChartDetails.signals;
      selectedSignalIds = this.chartsConfigurationHelperService.getSelectedSignalIdsById(chartSignalEntries, this.usableSignals());
    }

    this.selectedSignalIds.set([...selectedSignalIds]);
    const validConfiguration: boolean = this.verifyConfiguration();
    this.isConfigurationValid.emit(validConfiguration);
  }

  private verifyConfiguration(): boolean {
    if (!this.userDefinedWidget().userDefinedChartDetails.signals || this.userDefinedWidget().userDefinedChartDetails.signals.length === 0) {
      return false;
    }

    return this.verifyTimePeriod(this.userDefinedWidget().userDefinedChartDetails.rangeValue);
  }

  private verifyTimePeriod(timePeriod: number): boolean {
    return Number.isInteger(timePeriod) && timePeriod >= 1 && timePeriod <= ChartConstants.MAX_INTERVAL_MINUTES;
  }

  private initializeDragAndDropSubscriptionEvents() {
    this.subscription.add(this.dragulaService.drag(this.dragAndDropItems)
        .subscribe(({el}) => {
          el.classList.remove('ex-moved');
        })
    );
    this.subscription.add(this.dragulaService.drop(this.dragAndDropItems)
        .subscribe(({el}) => {
          el.classList.add('ex-moved');
        })
    );
    this.subscription.add(this.dragulaService.over(this.dragAndDropItems)
        .subscribe(({container}) => {
          container.classList.add('ex-over');
        })
    );
    this.subscription.add(this.dragulaService.out(this.dragAndDropItems)
        .subscribe(({container}) => {
          container.classList.remove('ex-over');
        })
    );
  }

  private setSelectedSignals() {
    if (this.userDefinedWidget().userDefinedChartDetails && this.userDefinedWidget().userDefinedChartDetails.signals) {
      const selectedSignals = this.userDefinedWidget().userDefinedChartDetails.signals;
      const selectedSignalIds: number[] = this.chartsConfigurationHelperService.getSelectedSignalIdsById(selectedSignals, this.usableSignals());
      this.selectedSignalIds.set([...selectedSignalIds]);
    }
  }
}
